import { Action, State, StateContext } from '@ngxs/store';
import { inject, Injectable, NgZone } from '@angular/core';
import {
  AcceptInvite,
  AddInvitation,
  DeleteInvitation,
  GetInvitationList,
  ResendInvitation,
} from './invitation.actions';

import { InvitationStateModel } from './invitation.model';
import { InvitationService } from './invitation.service';
import { catchError, tap, throwError } from 'rxjs';
import { NotificationService } from '../../app/services/notification.service';
import { Router } from '@angular/router';
import { HideSideMenu } from '../general-store/general.actions';
@State<InvitationStateModel>({
  name: 'Invitation',
  defaults: {
    error: null,
    invitations: [],
    isLoading: false,
    pagination: {
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
      totalPages: 0,
    },
    headers: [],
  },
})
@Injectable({
  providedIn: 'root',
})
export class InvitationState {
  private readonly invitationService = inject(InvitationService);
  private readonly notificationService = inject(NotificationService);
  private readonly ngZone = inject(NgZone);
  private readonly router = inject(Router);

  @Action(AddInvitation)
  addInvitation(
    { patchState, dispatch }: StateContext<InvitationStateModel>,
    action: AddInvitation,
  ) {
    patchState({ isLoading: true });
    return this.invitationService.addInvitation(action.payload).pipe(
      tap(async () => {
        patchState({
          isLoading: false,
        });
        dispatch(new HideSideMenu());
        this.notificationService.openSuccessToast('Invitation sent successfully!');
      }),
      catchError(async (error) => {
        patchState({ isLoading: false });
        return throwError(() => error);
      }),
    );
  }

  @Action(DeleteInvitation)
  deleteInvitation(
    { patchState, dispatch }: StateContext<InvitationStateModel>,
    action: DeleteInvitation,
  ) {
    patchState({ isLoading: true });
    return this.invitationService.deleteInvitation(action.id).pipe(
      tap(async () => {
        patchState({
          isLoading: false,
        });
        dispatch(new HideSideMenu());
        this.notificationService.openSuccessToast('Invitation deleted successfully!');
      }),
      catchError(async (_error) => {
        patchState({ isLoading: false });
      }),
    );
  }

  @Action(ResendInvitation)
  resendInvitation({ dispatch }: StateContext<InvitationStateModel>, action: ResendInvitation) {
    return this.invitationService.resendInvitation(action.id).pipe(
      tap(async () => {
        dispatch(new HideSideMenu());
        setTimeout(() => {
          this.notificationService.openSuccessToast('Invitation resent successfully!');
        }, 0);
      }),
      catchError(async (error) => {
        console.log(error);
      }),
    );
  }

  @Action(GetInvitationList)
  getInvitationList({ patchState }: StateContext<InvitationStateModel>, action: GetInvitationList) {
    patchState({ isLoading: true });
    return this.invitationService.getInvitationList(action.payload).pipe(
      tap(async (res) => {
        patchState({
          invitations: res.payload,
          pagination: res.pagination,
          headers: res.headers,
          isLoading: false,
        });
      }),
      catchError(async (_error) => {
        patchState({ isLoading: false });
      }),
    );
  }

  @Action(AcceptInvite)
  acceptInvite({ patchState }: StateContext<InvitationStateModel>, action: AcceptInvite) {
    patchState({ isLoading: true, error: null });
    return this.invitationService.acceptInvite(action.payload).pipe(
      tap(async () => {
        patchState({
          isLoading: false,
          error: null,
        });
        this.notificationService.openSuccessToast(
          'You have successfully signed up. Please login to continue',
        );
        this.ngZone.run(() => {
          this.router.navigate(['/login'], { replaceUrl: true });
        });
      }),
      catchError(async (error) => {
        patchState({ isLoading: false, error: error.error.error.message });
        return throwError(() => error);
      }),
    );
  }
}
